import { useContext } from 'react';
import { T, Var, DateTime, useGT } from 'gt-react';
import { CheckIcon, CopyIcon } from '@phosphor-icons/react';
import { usePaymentApprovals } from '@/api/rpc/hooks/finance/usePaymentApprovals';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AuthContext } from 'src/context/auth.context';
import { useViewMode } from 'src/context/view-mode.context';
import { isAdmin } from '@/utils/user';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';

interface PaymentApprovalsSectionProps {
  paymentId: string;
}

const DATETIME_OPTIONS = {
  dateStyle: 'medium',
  timeStyle: 'medium',
} as const;

export function PaymentApprovalsSection({
  paymentId,
}: PaymentApprovalsSectionProps) {
  const gt = useGT();
  const { approvals, isLoading, isError } = usePaymentApprovals(paymentId);
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const isAdminUser = isAdmin(currentUser, viewMode);
  const { copiedValue: copiedId, copy: copyId } = useCopyToClipboard();

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-muted-foreground">
        <T>Unable to load approvals.</T>
      </p>
    );
  }

  if (approvals.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        <T>No approvals recorded yet.</T>
      </p>
    );
  }

  return (
    <ul className="space-y-3">
      {approvals.map((approval) => {
        const isRevoked = Boolean(approval.revokedAt);
        return (
          <li
            key={approval.id}
            className="flex flex-col gap-1 rounded-md border border-border bg-muted/30 p-3"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">
                  {approval.userName ?? approval.userId}
                </span>
                {isRevoked ? (
                  <Badge variant="destructive">
                    <T>Revoked</T>
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="bg-green-50 text-green-800 border-green-200"
                  >
                    <T>Approved</T>
                  </Badge>
                )}
              </div>
              <span className="text-xs text-muted-foreground">
                <DateTime options={DATETIME_OPTIONS}>
                  {new Date(approval.approvedAt)}
                </DateTime>
              </span>
            </div>
            {isAdminUser && (
              <div className="flex items-center gap-1 text-[11px] text-muted-foreground font-mono">
                <span className="truncate">{approval.id}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  onClick={() => void copyId(approval.id)}
                  aria-label={gt('Copy approval ID', {
                    context: 'button aria label for copying approval ID',
                  })}
                >
                  {copiedId === approval.id ? (
                    <CheckIcon className="h-3 w-3" />
                  ) : (
                    <CopyIcon className="h-3 w-3" />
                  )}
                </Button>
              </div>
            )}
            {approval.comment && (
              <p className="text-xs text-muted-foreground italic">
                &ldquo;{approval.comment}&rdquo;
              </p>
            )}
            {isRevoked && (
              <div className="text-xs text-muted-foreground">
                <T>Revoked</T>
                {approval.revokedByName && (
                  <>
                    {' '}
                    <T>
                      by <Var>{approval.revokedByName}</Var>
                    </T>
                  </>
                )}
                {approval.revokedAt && (
                  <>
                    {' · '}
                    <DateTime options={DATETIME_OPTIONS}>
                      {new Date(approval.revokedAt)}
                    </DateTime>
                  </>
                )}
                {approval.revocationReason && (
                  <>
                    {' · '}
                    <span className="italic">{approval.revocationReason}</span>
                  </>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
